import { useRouter } from "next/router";
import { useEffect } from "react";

import CodeBlock from "@tiptap/extension-code-block";
import { Document } from "@tiptap/extension-document";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import Strike from "@tiptap/extension-strike";
import {
  EditorContent as ReadOnlyEditorContent,
  useEditor,
} from "@tiptap/react";

import ReadAloudButton, {
  ButtonTypes,
} from "@/view/ReadAloudButton/ReadAloudButton";
import useReadAloudMode from "@/view/ReadAloudControls/useReadAloudMode";
import { ReadAloudContentModes } from "@/view/ReadAloudControls/useReadAloudMode/constants";
import { HardBreakBlock } from "@/view/TiptapArticleBody/extensions/HardBreakBlock";
import { HeadingBlock } from "@/view/TiptapArticleBody/extensions/HeadingBlock";
import IgnoreBlock from "@/view/TiptapArticleBody/extensions/IgnoreBlock";
import { Image } from "@/view/TiptapArticleBody/extensions/Image";
import {
  OrderedList,
  OrderedListItem,
  UnorderedList,
  UnorderedListItem,
} from "@/view/TiptapArticleBody/extensions/List";
import {
  ListenModeHighlightDecorator,
  listenModeHighlightDecoratorPluginKey,
} from "@/view/TiptapArticleBody/extensions/ListenModeHighlightDecorator";
import { ParagraphBlock } from "@/view/TiptapArticleBody/extensions/ParagraphBlock";
import { PreformattedBlock } from "@/view/TiptapArticleBody/extensions/PreformattedBlock";
import QuoteBlock from "@/view/TiptapArticleBody/extensions/QuoteBlock";
import { Core } from "@/view/TiptapArticleBody/extensions/core";
import { containerCss, rootCss } from "./QuizQuestionText.style";

interface INode {
  text?: string;
  type: string;
  content?: INode[];
}

interface IProps {
  id: string;
  isReadAloudButtonActive?: boolean;
  isStatic: boolean;
  questionPosition: `${bigint}`;
  text: INode | null;
}

export const QuizQuestionText = ({
  id,
  isReadAloudButtonActive = false,
  isStatic = false,
  questionPosition,
  text,
}: IProps) => {
  const router = useRouter();
  const {
    query: { levelId },
  } = router;

  const {
    currentSpeech,
    isReadAloudModeActive,
    readAloudContentMode,
    currentMark,
  } = useReadAloudMode();

  const READ_ALOUD_CONTENT_MODE = ReadAloudContentModes.QUIZ;

  const editor = useEditor(
    {
      editorProps: {
        attributes: {
          class: "QuizQuestionText-text",
          "data-testid": "QuizQuestionText-Editor",
        },
      },
      extensions: [
        CodeBlock,
        Core,
        Document,
        HardBreakBlock,
        HeadingBlock.configure({ disableTranslation: true }),
        HorizontalRule,
        ListenModeHighlightDecorator,
        Image,
        IgnoreBlock,
        OrderedList.configure({ disableTranslation: true }),
        OrderedListItem,
        UnorderedList.configure({ disableTranslation: true }),
        UnorderedListItem,
        ParagraphBlock.configure({
          disableTranslation: true,
          useLegacyHighlighting: false,
        }),
        PreformattedBlock.configure({ disableTranslation: true }),
        QuoteBlock.configure({ disableTranslation: true }),
        Strike,
      ],
      content: text,
      editable: false,
    },
    [levelId, questionPosition, JSON.stringify(text)],
  );

  // Dispatch the transaction and update the editor state (apply highlighting to selected words)
  useEffect(() => {
    if (editor) {
      const metaData =
        !isReadAloudModeActive ||
        readAloudContentMode !== READ_ALOUD_CONTENT_MODE
          ? {}
          : { ...currentMark };
      const transaction = editor.state.tr.setMeta(
        listenModeHighlightDecoratorPluginKey,
        metaData,
      );
      editor.view.dispatch(transaction);
    }
  }, [
    JSON.stringify(currentMark),
    isReadAloudModeActive,
    levelId,
    questionPosition,
    JSON.stringify(text),
  ]);

  // Pause previous quiz question if we're on a new question
  useEffect(() => {
    if (readAloudContentMode === READ_ALOUD_CONTENT_MODE && currentSpeech) {
      currentSpeech.pauseAudio();
    }
  }, [questionPosition]);

  if (!text || !editor) return null;

  return (
    <div css={containerCss} data-testid="quizQuestionEditorWrapper">
      {isStatic ? (
        <div css={rootCss} id={id} data-testid="quizQuestionContent-static">
          <ReadOnlyEditorContent
            editor={editor}
            data-testid="readOnlyEditorQuizContent"
          />
        </div>
      ) : (
        <legend
          css={rootCss}
          id={id}
          data-testid="quizQuestionContent-interactive"
        >
          <ReadOnlyEditorContent
            editor={editor}
            data-testid="readOnlyEditorQuizContent"
          />
        </legend>
      )}
      {isReadAloudButtonActive && (
        <ReadAloudButton
          buttonContentMode={READ_ALOUD_CONTENT_MODE}
          position={questionPosition}
          text={text}
          type={ButtonTypes.question}
        />
      )}
    </div>
  );
};

export default QuizQuestionText;
